import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LazyMotion, AnimatePresence, motion, domAnimation } from "./lib/motion";
import type {
  AnalysisContext,
  AppState,
  SourceFile,
  Speaker,
  Transcript,
} from "./types";
import Upload from "./components/Upload";
import ContextForm from "./components/ContextForm";
import Thinking from "./components/Thinking";
import ErrorModal from "./components/ErrorModal";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  CancelledError,
  TimeoutError,
  analyzeTranscripts,
  type AnalysisStatus,
} from "./services/gemini";
import { I18nProvider, useI18n } from "./i18n/I18nProvider";
import LoginPage from "./components/LoginPage";
import { RequireAuth } from "./components/RequireAuth";
import {
  EMPTY_SESSION,
  clearSession as clearStoredSession,
  loadSession,
  saveSession,
  type PersistedSession,
} from "./lib/session";

const Map = lazy(() => import("./components/Map"));

function MainApp() {
  const { t } = useI18n();
  const [session, setSession] = useState<PersistedSession>(() => loadSession());
  const [state, setState] = useState<AppState>(session.mapData ? "map" : "upload");
  const [error, setError] = useState<string | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<string>("");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    document.title = "Sherlock | Strategic Insight Mapping";
  }, []);

  useEffect(() => {
    if (state !== "upload") return;
    const idle = (window as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    const handle = idle
      ? idle(() => {
          void import("./components/Map");
        })
      : window.setTimeout(() => {
          void import("./components/Map");
        }, 1500);
    return () => {
      const cancel = (window as { cancelIdleCallback?: (id: number) => void })
        .cancelIdleCallback;
      if (idle && cancel) cancel(handle as number);
      else window.clearTimeout(handle as number);
    };
  }, [state]);

  useEffect(() => {
    saveSession(session);
  }, [session]);

  const updateSession = useCallback(
    (next: PersistedSession | ((prev: PersistedSession) => PersistedSession)) => {
      setSession((prev) => (typeof next === "function" ? next(prev) : next));
    },
    [],
  );

  const runAnalysis = useCallback(
    async (
      transcripts: Transcript[],
      context: AnalysisContext,
      sources: SourceFile[],
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState("thinking");
      setAnalysisStatus("Initializing analysis...");
      try {
        const result = await analyzeTranscripts(
          { transcripts, context },
          (status: AnalysisStatus) => setAnalysisStatus(status.message),
          controller.signal,
        );
        if (controller.signal.aborted) return;
        updateSession({
          schemaVersion: session.schemaVersion,
          transcripts,
          sources,
          context,
          mapData: result,
        });
        setState("map");
      } catch (err) {
        if (err instanceof CancelledError || controller.signal.aborted) {
          setState("upload");
          return;
        }
        console.error(err);
        const message =
          err instanceof TimeoutError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to analyze documents. Please try again.";
        setError(message);
        setState("upload");
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [session.schemaVersion, updateSession],
  );

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState("upload");
  }, []);

  const handleUpload = (payload: {
    transcripts: Transcript[];
    sources: SourceFile[];
  }) => {
    updateSession((prev) => ({
      ...prev,
      transcripts: payload.transcripts,
      sources: payload.sources,
      mapData: null,
    }));
    setState("context");
  };

  const handleDemo = async () => {
    const { DEMO_DATA, DEMO_TRANSCRIPTS } = await import("./constants/demoData");
    updateSession({
      schemaVersion: session.schemaVersion,
      transcripts: DEMO_TRANSCRIPTS,
      sources: [],
      context: null,
      mapData: DEMO_DATA,
    });
    setState("map");
  };

  const handleContextSubmit = (data: AnalysisContext) => {
    updateSession((prev) => ({ ...prev, context: data }));
    void runAnalysis(session.transcripts, data, session.sources ?? []);
  };

  const handleUploadAdditional = (payload: {
    transcripts: Transcript[];
    sources: SourceFile[];
  }) => {
    updateSession((prev) => ({
      ...prev,
      transcripts: payload.transcripts,
      sources: payload.sources,
      mapData: null,
    }));
    setState("context");
  };

  const handleUpdateSpeaker = (updatedSpeaker: Speaker) => {
    if (!session.mapData) return;
    updateSession((prev) =>
      prev.mapData
        ? {
            ...prev,
            mapData: {
              ...prev.mapData,
              speakers: prev.mapData.speakers.map((s) =>
                s.id === updatedSpeaker.id ? updatedSpeaker : s,
              ),
            },
          }
        : prev,
    );
  };

  const handleReset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearStoredSession();
    setSession(EMPTY_SESSION);
    setState("upload");
  };

  return (
    <div className="bg-bg text-ink min-h-screen font-sans selection:bg-emerald-500/20">
      <div className="atmosphere" aria-hidden="true" />
      {state !== "map" && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-3 sm:top-6 sm:right-6">
          <a
            href="/api/auth/logout"
            className="text-[11px] font-medium tracking-wide text-zinc-500 uppercase transition-colors hover:text-zinc-800 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none"
          >
            Sign out
          </a>
        </div>
      )}
      <main id="main-content">
        <AnimatePresence mode="wait">
          {state === "upload" && (
            <motion.section
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex min-h-screen items-center justify-center p-6"
              aria-label="Document upload"
            >
              <Upload
                onUpload={handleUpload}
                onDemo={handleDemo}
                onResume={session.mapData ? () => setState("map") : undefined}
              />
            </motion.section>
          )}

          {state === "context" && (
            <motion.section
              key="context"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="flex min-h-screen items-center justify-center p-6"
              aria-label="Analysis context"
            >
              <ContextForm onSubmit={handleContextSubmit} />
            </motion.section>
          )}

          {state === "thinking" && (
            <motion.section
              key="thinking"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="min-h-screen"
              aria-live="polite"
              aria-busy="true"
            >
              <Thinking status={analysisStatus} onCancel={handleCancel} />
            </motion.section>
          )}

          {state === "map" && session.mapData && (
            <motion.section
              key="map"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="h-screen w-full overflow-hidden"
              aria-label="Strategic map"
            >
              <Suspense
                fallback={
                  <div className="flex h-screen items-center justify-center">
                    <div className="text-zinc-400 italic">Loading map…</div>
                  </div>
                }
              >
                <Map
                  requestedViews={session.context?.requestedViews ?? []}
                  data={session.mapData}
                  transcripts={session.transcripts}
                  sources={session.sources}
                  onReset={handleReset}
                  onUpdateSpeaker={handleUpdateSpeaker}
                  onUploadAdditional={handleUploadAdditional}
                />
              </Suspense>
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      <ErrorModal
        error={error}
        onClose={() => setError(null)}
        onRetry={() => {
          setError(null);
          if (session.context)
            void runAnalysis(
              session.transcripts,
              session.context,
              session.sources ?? [],
            );
        }}
      />
      <span className="sr-only">{t("app.subtitle")}</span>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LazyMotion features={domAnimation} strict>
        <BrowserRouter>
          <I18nProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/*"
                element={
                  <RequireAuth>
                    <MainApp />
                  </RequireAuth>
                }
              />
            </Routes>
          </I18nProvider>
        </BrowserRouter>
      </LazyMotion>
    </ErrorBoundary>
  );
}
